import { useEffect, useMemo, useState, useCallback } from "react";

import {
    getLoopSlippage,
    LoopPositionExpanded,
    useUnwindQuoteAndIxs,
    getZcCollateral,
    getZcLedger,
    getDefaultWindType,
    isWhirlpoolLoop,
    isCollateralSplMint,
    LoopRouteResponseExpanded,
    calculateLoopFairMarketValue,
    useActiveWallet,
    useRepayLoanTransaction,
    useTokenListMetadataByMints,
    useUnwindStats,
    ProfitLossStats,
    AbfLoanExpanded,
    LoanCollateralExpanded
} from "@bridgesplit/abf-react";
import {
    BalanceChanges,
    bsMath,
    formatTokenAmount,
    formatUsd,
    uiAmountToLamports,
    Result,
    LOADING_ERROR
} from "@bridgesplit/utils";
import {
    Column,
    OutlinedCard,
    Row,
    StatColumn,
    Text,
    TooltipText,
    Icon,
    MEDIA,
    hideScrollbarSx,
    useAppPalette
} from "@bridgesplit/ui";
import { COPY } from "app/constants";
import { RepayLoanTxnParams, TokenListMetadata, LoopWindType } from "@bridgesplit/abf-sdk";
import { useMediaQuery } from "@mui/material";
import { trackSubmitRepay } from "app/hooks";
import { TrackTransactionEvent, TransactionTypes } from "app/types";

import { ActionProps } from "./types";
import {
    AppButton,
    Slippage,
    useLoopUnwindAlerts,
    LoopDisplayType,
    RefreshButton,
    TokenAmountWithUsd,
    PandoraQuoteError
} from "../../common";
import { ReceiveSwapButton } from "./common";
import { createRepaymentParams } from "./payments/util";
import { MakeRepaymentParams } from "./payments/types";
import { useTransactionSender } from "../../transactions";
import { ExecutionDetails, useExecutionDetails } from "../../loop/execution-detail";
import { ExecutionDetailsLabels } from "../../loop/execution-detail/types";

const wideRowStyle = {
    whiteSpace: "nowrap",
    overflow: "auto",
    maxWidth: "300px",
    ...hideScrollbarSx
};

const tokenFormatOptions = { compactLargeNumbers: true, short: true };
const textVariant = "body2";

export default function LoopUnwind({ loopPosition }: ActionProps) {
    const { slippagePresetsUi, defaultSlippageUi } = getLoopSlippage(loopPosition?.loopExpanded);
    const { slippagePercentDecimals, slippageWrapperProps, slippageButtonProps } = Slippage.useController({
        defaultSlippageUi,
        presetsUi: slippagePresetsUi
    });

    const { windType, displayType, setDisplayTypeHandler, setWindType } = useDisplayController(loopPosition);

    const loanExpanded = loopPosition?.loanExpanded;
    const loanCollateral = getZcCollateral(loopPosition?.loanExpanded);
    const ledger = getZcLedger(loopPosition?.loanExpanded);
    const debtToRepay = ledger?.ledgerDebt.total;

    const {
        data: externalQuote,
        isError,
        isLoading: externalQuoteLoading,
        isFetching: externalQuoteFetching,
        refetch: refetchExternalQuote,
        error: externalQuoteError
    } = useUnwindQuoteAndIxs({
        loopPositionExpanded: loopPosition,
        slippagePercentDecimals: slippagePercentDecimals ?? 0,
        windType,
        debtToRepay,
        loanCollateral,
        collateralAmount: loanCollateral?.amount,
        loanExpanded: loopPosition?.loanExpanded
    });

    const repayParams = useMemo(
        () =>
            createRepaymentParams({
                principalPaymentUiAmount: debtToRepay,
                collateralPaymentUiAmount: loanCollateral?.amount,
                isFullRepay: true,
                repayType: windType,
                collateral: loanCollateral,
                loanExpanded,
                quote: externalQuote,
                isLoopUnwind: true
            }),
        [debtToRepay, loanCollateral, loanExpanded, externalQuote, windType]
    );

    const { activeWallet } = useActiveWallet();
    const {
        previewBalances,
        repayTxnParams,
        repayTx,
        error: repaySimulationError
    } = useRepayPreviewBalances({
        loanExpanded,
        loanCollateral,
        activeWallet,
        debtToRepay,
        repayParams,
        externalQuote
    });
    const loopUnwindError = isError || repaySimulationError;

    const send = useTransactionSender();
    const closeLoop = useCallback(async () => {
        if (!repayTxnParams || !loanExpanded || !repayTx) return Result.errFromMessage(LOADING_ERROR);
        return await send(repayTx, repayTxnParams, {
            description: COPY.UNWIND_TERM,
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitRepay,
                params: trackSubmitRepay(repayTxnParams, loanExpanded.principalMetadata)
            }
        });
    }, [repayTxnParams, repayTx, send, loanExpanded]);

    const summary = useUnwindStats({
        loopPosition,
        externalQuote,
        windType,
        previewBalances
    });

    const { unwindWarningMessage } = useLoopUnwindAlerts({
        loopPositionExpanded: loopPosition,
        summary,
        variant: "route"
    });

    const slippageButton = { ...slippageButtonProps, showSlippageValue: false };

    const { primaryToAlpha } = useAppPalette();

    const windTypeEnabled = isCollateralSplMint(loanCollateral);

    const quoteLoading = externalQuoteLoading || externalQuoteFetching;
    const loading = quoteLoading || !summary;

    return (
        <Slippage.Wrapper {...slippageWrapperProps}>
            <Row spaceBetween>
                <Text variant="h4">{COPY.UNWIND_TERM}</Text>
                <Row spacing={0.75}>
                    <Slippage.Button {...slippageButton} />
                    <RefreshButton refresh={refetchExternalQuote} />
                    {windTypeEnabled && (
                        <ReceiveSwapButton
                            principalToken={loopPosition?.loopExpanded?.principalToken}
                            collateralToken={loopPosition?.loopExpanded?.collateralToken}
                            receiveType={windType}
                            setReceiveType={setWindType}
                            tokenPreText="Receive"
                        />
                    )}
                </Row>
            </Row>

            <ReceiveStats
                summary={summary}
                windType={windType}
                loopPosition={loopPosition}
                loading={loading}
                error={loopUnwindError}
            />
            <OutlinedCard hideBorder backgroundColor={primaryToAlpha}>
                <SecondaryStats
                    loading={loading}
                    error={loopUnwindError}
                    loopPosition={loopPosition}
                    summary={summary}
                    windType={windType}
                    slippagePercentDecimals={slippagePercentDecimals}
                    displayType={displayType}
                    setDisplayTypeHandler={setDisplayTypeHandler}
                    externalQuote={externalQuote}
                />
            </OutlinedCard>
            {unwindWarningMessage ? (
                <Text variant="body2" color="caption">
                    {unwindWarningMessage}
                </Text>
            ) : null}
            {loopUnwindError && isError && <PandoraQuoteError externalQuoteError={externalQuoteError} />}
            <AppButton
                disabled={!summary || loopUnwindError}
                isTransaction
                transactionType={TransactionTypes.LoopUnwind}
                asyncCta={{ onClickWithResult: closeLoop }}
            >
                {COPY.UNWIND_TERM}
            </AppButton>
        </Slippage.Wrapper>
    );
}

function useRepayPreviewBalances({
    loanExpanded,
    loanCollateral,
    activeWallet,
    debtToRepay,
    repayParams,
    externalQuote
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loanCollateral: LoanCollateralExpanded | undefined;
    activeWallet: string | undefined;
    debtToRepay: number | undefined;
    repayParams: MakeRepaymentParams;
    externalQuote: LoopRouteResponseExpanded | undefined;
}) {
    const [previewBalances, setPreviewBalances] = useState<BalanceChanges | undefined>();
    const [error, setError] = useState<boolean>(false);
    const repayTx = useRepayLoanTransaction(true);
    const repayTxnParams = useMemo<RepayLoanTxnParams | undefined>(() => {
        if (
            !loanExpanded ||
            !loanCollateral ||
            !activeWallet ||
            debtToRepay === undefined ||
            !repayParams.mintToReceive ||
            !externalQuote
        )
            return undefined;
        return {
            loan: loanExpanded.loan.address,
            repayParams: [
                {
                    amount: uiAmountToLamports(debtToRepay, loanExpanded.principalMetadata.decimals),
                    ledgerIndex: 0,
                    repayAll: true
                }
            ],
            collateralWithdrawalParams: [
                {
                    collateralMint: loanCollateral.assetMint,
                    amount: uiAmountToLamports(loanCollateral.amount, loanCollateral.metadata.decimals),
                    closeIfEligible: true
                }
            ],
            cpiIxs: repayParams.cpiIxs,
            cpiLuts: repayParams.cpiLuts,
            cpiSigners: repayParams.cpiSigners,
            mintToReceive: repayParams.mintToReceive,
            userPublicKey: activeWallet
        };
    }, [loanExpanded, loanCollateral, activeWallet, debtToRepay, repayParams, externalQuote]);

    useEffect(() => {
        setPreviewBalances(undefined);
        setError(false);
        async function preview() {
            if (!repayTxnParams) return;
            const result = await repayTx.getTransactionsWithParamsAndBalanceChanges({
                ...repayTxnParams,
                simulate: true
            });
            if (result.isErr()) {
                setPreviewBalances(undefined);
                setError(true);
            }
            if (result.isOk()) {
                setPreviewBalances(result.unwrap().balanceChanges);
            }
        }
        preview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [repayTxnParams]);

    return { previewBalances, repayTxnParams, repayTx, error };
}

function ReceiveStats({
    summary,
    windType,
    loopPosition,
    loading,
    error
}: {
    summary: ProfitLossStats | undefined;
    windType: LoopWindType;
    loopPosition: LoopPositionExpanded | undefined;
    loading: boolean;
    error: boolean;
}) {
    const tokenStats = summary?.tokenStats;
    const balanceChanges = tokenStats?.balanceChanges ?? {};
    const mints = Object.keys(balanceChanges);
    const { getMetadata } = useTokenListMetadataByMints(mints);

    const receiveOnCloseWarning = getReceiveOnCloseWarning({ loopPosition, windType });

    if (error) {
        return (
            <Column spacing={1}>
                <Row spaceBetween>
                    <Row spacing={0.5}>
                        <Text variant={textVariant} color="caption">
                            Unable to compute route.
                        </Text>
                        <TooltipText helpText="There was an issue loading an automatic unwind route due to insufficient liquidity in an underlying platform." />
                    </Row>
                </Row>
            </Column>
        );
    }

    return (
        <Column spacing={1}>
            <Row spacing={0.5}>
                <Row spacing={0.5}>
                    <Text variant={textVariant} color="caption">
                        Receive on close
                    </Text>
                    <TooltipText helpText="Worst-case estimate of what you'll receive after repaying debt, including slippage and fees" />
                </Row>
                {!loading && receiveOnCloseWarning && (
                    <TooltipText helpText={receiveOnCloseWarning} icon={false} color="warning">
                        <Icon type="warning" />
                    </TooltipText>
                )}
            </Row>
            <Row spacing={0.5}>
                <Row spacing={0.5} wrap>
                    {Object.entries(balanceChanges).map(([mint, amount], index, array) => {
                        const token = getMetadata(mint);
                        const amountUsd =
                            tokenStats?.tokenPrices?.[mint] !== undefined
                                ? amount * (tokenStats.tokenPrices?.[mint] ?? 0)
                                : undefined;
                        return (
                            <Row key={mint} spacing={0.5}>
                                <TokenAmountWithUsd
                                    variant="h3"
                                    tokenSize="md"
                                    amount={amount}
                                    amountUsd={amountUsd}
                                    token={token}
                                    loading={loading}
                                />
                                {!loading && index < array.length - 1 && <Text color="caption">+</Text>}
                            </Row>
                        );
                    })}
                    {loading && Object.entries(balanceChanges).length === 0 && (
                        <TokenAmountWithUsd
                            variant="h3"
                            tokenSize="md"
                            amount={undefined}
                            amountUsd={undefined}
                            token={undefined}
                            loading
                        />
                    )}
                </Row>
            </Row>
        </Column>
    );
}

export function useDisplayController(loopPosition: LoopPositionExpanded | undefined) {
    const isMobile = useMediaQuery(MEDIA.SM.below);

    const defaultWindType = getDefaultWindType(loopPosition);
    const [windType, setWindType] = useState<LoopWindType>(defaultWindType ?? LoopWindType.Collateral);
    const [displayType, setDisplayType] = useState<LoopDisplayType>(LoopDisplayType.UsdCostBasis);

    const setDisplayTypeHandler = () => {
        if (isMobile) return;
        setDisplayType(
            displayType === LoopDisplayType.TokenCostBasis
                ? LoopDisplayType.UsdCostBasis
                : LoopDisplayType.TokenCostBasis
        );
    };

    return {
        windType,
        setWindType,
        displayType,
        setDisplayTypeHandler
    };
}

function getPrincipalOnlyUnwindStats({
    loopPosition,
    displayType,
    setDisplayTypeHandler
}: {
    loopPosition: LoopPositionExpanded | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
}) {
    const stats = summarizeCollateralRepayStats(loopPosition, LoopWindType.Principal);
    if (!stats) return [];

    return [
        {
            caption: "Collateral",
            value: (
                <MultipleStatDisplay
                    {...stats.collateral}
                    displayType={displayType}
                    setDisplayTypeHandler={setDisplayTypeHandler}
                />
            )
        },
        {
            caption: "Repay",
            value: (
                <Row spacing={0.5}>
                    <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()}>
                        {displayType === LoopDisplayType.TokenCostBasis
                            ? formatTokenAmount(stats.repay.primaryAmount, {
                                  symbol: stats.repay.primaryToken?.symbol,
                                  ...tokenFormatOptions
                              })
                            : formatUsd(stats.repay.primaryUsdAmount)}
                    </Text>
                </Row>
            )
        }
    ];
}

function MultipleStatDisplay({
    primaryAmount,
    primaryUsdAmount,
    primaryToken,
    secondaryAmount,
    secondaryToken,
    displayType,
    setDisplayTypeHandler
}: StatDisplay) {
    return (
        <Row sx={wideRowStyle} spacing={0.5}>
            <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()}>
                {displayType === LoopDisplayType.TokenCostBasis
                    ? formatTokenAmount(primaryAmount, {
                          symbol: primaryToken?.symbol,
                          ...tokenFormatOptions
                      })
                    : formatUsd(primaryUsdAmount)}
            </Text>
            {displayType === LoopDisplayType.TokenCostBasis && secondaryAmount !== undefined && (
                <Text variant={textVariant} onClick={() => setDisplayTypeHandler()} color="caption">
                    {formatTokenAmount(secondaryAmount, {
                        symbol: secondaryToken?.symbol,
                        ...tokenFormatOptions
                    })}
                </Text>
            )}
        </Row>
    );
}

interface StatDisplay {
    primaryAmount: number | undefined;
    primaryUsdAmount: number | undefined;
    primaryToken: TokenListMetadata | undefined;
    secondaryAmount?: number | undefined;
    secondaryToken?: TokenListMetadata | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
}

function summarizeCollateralRepayStats(loopPosition: LoopPositionExpanded | undefined, windType: LoopWindType) {
    if (!loopPosition) return undefined;

    const collateral = getZcCollateral(loopPosition.loanExpanded);
    const ledger = getZcLedger(loopPosition.loanExpanded);
    const collateralAmount = collateral?.amount;
    const principalPrice = loopPosition.loopExpanded?.principalOracle?.usdPrice;
    const principalToken = loopPosition.loopExpanded?.principalToken;
    const loopCollateral = getZcCollateral(loopPosition.loanExpanded);
    const collateralPrice = (loopCollateral?.usdPrice ?? 0) + (loopCollateral?.uncertainty || 0);
    const totalDebt = ledger?.ledgerDebt.total;
    const totalDebtUsd = bsMath.mul(totalDebt, principalPrice);
    const collateralValueUsd = bsMath.mul(collateralAmount, loopCollateral && collateralPrice);

    if (windType === LoopWindType.Principal) {
        const principalAmountOfCollateral = bsMath.div(collateralValueUsd, principalPrice);

        return {
            collateral: {
                primaryAmount: principalAmountOfCollateral,
                primaryUsdAmount: collateralValueUsd,
                primaryToken: principalToken,
                secondaryAmount: collateralAmount,
                secondaryToken: collateral?.metadata
            },
            repay: {
                primaryAmount: totalDebt,
                primaryUsdAmount: totalDebtUsd,
                primaryToken: principalToken
            }
        };
    }

    const debtToRepayInDollars = bsMath.mul(principalPrice, totalDebt);
    const collateralAmountOfDebt = bsMath.div(debtToRepayInDollars, collateralPrice);

    return {
        collateral: {
            primaryAmount: collateralAmount,
            primaryUsdAmount: collateralValueUsd,
            primaryToken: collateral?.metadata
        },
        repay: {
            primaryAmount: collateralAmountOfDebt,
            primaryUsdAmount: debtToRepayInDollars,
            primaryToken: loopPosition.loopExpanded?.collateralToken,
            secondaryAmount: totalDebt,
            secondaryToken: principalToken
        }
    };
}

function getCollateralUnwindStats({
    loopPosition,
    displayType,
    setDisplayTypeHandler
}: {
    loopPosition: LoopPositionExpanded | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
}) {
    const stats = summarizeCollateralRepayStats(loopPosition, LoopWindType.Collateral);
    if (!stats) return [];

    return [
        {
            caption: "Collateral",
            value: (
                <Row spacing={0.5}>
                    <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()}>
                        {displayType === LoopDisplayType.TokenCostBasis
                            ? formatTokenAmount(stats.collateral.primaryAmount, {
                                  symbol: stats.collateral.primaryToken?.symbol,
                                  ...tokenFormatOptions
                              })
                            : formatUsd(stats.collateral.primaryUsdAmount)}
                    </Text>
                </Row>
            )
        },
        {
            caption: "Repay",
            value: (
                <MultipleStatDisplay
                    {...stats.repay}
                    displayType={displayType}
                    setDisplayTypeHandler={setDisplayTypeHandler}
                />
            )
        }
    ];
}

function SecondaryStats({
    loading,
    error,
    loopPosition,
    windType,
    summary,
    slippagePercentDecimals,
    displayType,
    setDisplayTypeHandler,
    externalQuote
}: {
    loading: boolean;
    error: boolean;
    loopPosition: LoopPositionExpanded | undefined;
    windType: LoopWindType | undefined;
    summary: ProfitLossStats | undefined;
    slippagePercentDecimals: number | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
    externalQuote: LoopRouteResponseExpanded | undefined;
}) {
    const unwindStats = useMemo(() => {
        if (windType === LoopWindType.Principal) {
            return getPrincipalOnlyUnwindStats({ loopPosition, displayType, setDisplayTypeHandler });
        }
        return getCollateralUnwindStats({ loopPosition, displayType, setDisplayTypeHandler });
    }, [loopPosition, windType, displayType, setDisplayTypeHandler]);

    const ledger = getZcLedger(loopPosition?.loanExpanded);
    const debtAmount = ledger?.ledgerDebt.total;
    const collateral = getZcCollateral(loopPosition?.loanExpanded);
    const collateralAmount = collateral?.amount;
    const collateralOraclePrice = loopPosition?.userLoopInfo?.collateralOracle?.usdPrice;
    const principalOraclePrice = loopPosition?.loopExpanded?.principalOracle?.usdPrice;

    const balanceChanges = summary?.tokenStats?.balanceChanges;
    const collateralMint = getZcCollateral(loopPosition?.loanExpanded)?.assetIdentifier;
    const principalMint = loopPosition?.loanExpanded?.principalMetadata?.mint;
    const receiveCollateralAmount = collateralMint ? balanceChanges?.[collateralMint] ?? 0 : 0;
    const leftOverPrincipal = principalMint ? balanceChanges?.[principalMint] ?? 0 : 0;

    const fairMarketValue = calculateLoopFairMarketValue({
        collateralAmount: collateralAmount,
        debtAmount: debtAmount,
        collateralOraclePrice: collateralOraclePrice,
        principalOraclePrice: principalOraclePrice
    });

    // this is the total impact of the swap, including fees, slippage, price impact, and oracle price deviation
    const executionDetails = useExecutionDetails({
        principalOracle: loopPosition?.loopExpanded?.principalOracle,
        slippagePercentDecimals,
        externalQuote,
        receiveUsdAmount: summary?.tokenStats?.receiveUsdAmount,
        leftOverPrincipal,
        fairMarketValue
    });

    const labelOverrides = getLabelOverridesForUnwind(windType, loopPosition?.loopExpanded?.principalToken.symbol);

    return (
        <Column spacing={0.5}>
            <StatColumn variant={textVariant} spacing={0.5} stats={unwindStats} loading={loading} />
            {!error && (
                <ExecutionDetails
                    displayType={displayType}
                    setDisplayTypeHandler={setDisplayTypeHandler}
                    executionDetails={executionDetails}
                    loading={loading}
                    receiveTokenAmount={receiveCollateralAmount}
                    leftOverPrincipal={leftOverPrincipal}
                    displayTokenType={windType}
                    slippagePercentDecimals={slippagePercentDecimals}
                    externalQuote={externalQuote}
                    collateralUsdPrice={collateralOraclePrice}
                    principalUsdPrice={principalOraclePrice}
                    principalToken={loopPosition?.loopExpanded?.principalToken}
                    collateralToken={loopPosition?.loopExpanded?.inputToken}
                    labelOverrides={labelOverrides}
                    textVariant="body2"
                    loopExpanded={loopPosition?.loopExpanded}
                />
            )}
        </Column>
    );
}

function getReceiveOnCloseWarning({
    loopPosition,
    windType
}: {
    loopPosition: LoopPositionExpanded | undefined;
    windType: LoopWindType | undefined;
}): string | undefined {
    let receiveOnCloseWarning: string | undefined;
    const isOrcaLoop = isWhirlpoolLoop(loopPosition?.loopExpanded);

    if (windType === LoopWindType.Principal && !isOrcaLoop) {
        receiveOnCloseWarning = `Closing to ${loopPosition?.loopExpanded?.principalToken.symbol} increases execution impact`;
    }

    return receiveOnCloseWarning;
}

function getLabelOverridesForUnwind(
    windType: LoopWindType | undefined,
    principalTokenSymbol?: string
): Partial<ExecutionDetailsLabels> {
    return {
        executionImpact: {
            caption: "Execution impact",
            tooltip:
                windType === LoopWindType.Collateral
                    ? `Combined worst-case impact of slippage, fees, price impact, and market price on your expected return. Accounts for any additional ${principalTokenSymbol} received`
                    : `Combined worst-case impact of slippage, fees, price impact, and market price on your expected return.`
        },
        swapDifference: {
            caption: "Swap difference",
            tooltip:
                windType === LoopWindType.Collateral
                    ? `Adjustment due to difference in oracle price and swap price. You may receive ${principalTokenSymbol} if there is a mismatch in the estimated and executed price.`
                    : `Adjustment due to difference in oracle price and swap price`
        }
    };
}
